import { useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api/client';
import { API_ITEMS_URL } from '../utils';
import { normalizeId } from '../utils/ids';
import { collectLookupIds } from '../utils/lookups';
import { markNotReadable, markReadable, parseNotReadableHeader } from '../utils/linkedRecords';

const BATCH_RESOLVE_LIMIT = 100;

/**
 * Resolves items referenced by card-lookup elements via the batch
 * `?ids=` endpoint, and returns an id -> item map covering both the
 * items passed in and everything resolved so far.
 *
 * Resolved items are scanned too, so nested references (a linked item
 * whose title templates another link) also resolve. The attempted-ids set
 * keeps this from looping or re-fetching ids the server omitted. The
 * server names those in `X-Not-Readable`; they go to the app-wide registry
 * in utils/linkedRecords, and a chip for one says "No longer available".
 *
 * `items` is the seed pool — a page of items, or items an endpoint already
 * returned alongside its own payload (as `/related` does). Seeding what is
 * already in hand means only the genuinely missing ids are fetched. Pass a
 * referentially stable array; it drives the fetch effect.
 *
 * The returned map is what `createLookupResolver` resolves against, so pairing
 * the two is what makes a nested lookup render a label instead of a raw id.
 *
 * `resetKey` (the collection or item id) clears the cache when it changes.
 * `enabled` should be false until card definitions are available. `entries` also
 * resolves the lookup columns of repeating groups (the print route, KPR-1863).
 */
export default function useLinkedItems({ items, getElementsForCard, enabled = true, resetKey, entries = false }) {
	const [resolvedItemsById, setResolvedItemsById] = useState({});
	const attemptedIdsRef = useRef(new Set());
	// Bumped whenever the pool resets, so an in-flight batch can tell whether it
	// still belongs to the item/collection being viewed.
	const generationRef = useRef(0);
	// The pool's batches in flight, cancelled together when the pool resets
	// (another record, another collection) or the caller unmounts: nothing
	// would read their answers. A dependency change does NOT cancel them —
	// see below.
	const controllerRef = useRef(null);

	useEffect(() => {
		generationRef.current += 1;
		controllerRef.current?.abort();
		controllerRef.current = new AbortController();
		setResolvedItemsById({});
		attemptedIdsRef.current = new Set();
	}, [resetKey]);
	useEffect(() => () => controllerRef.current?.abort(), []);

	// A record in hand IS readable: one the registry tombstoned (a link that
	// ended, then came back) leaves it the moment it arrives here — a list
	// page, a /related payload — not only when a batch resolve returns it,
	// which never happens for an id the pool already holds.
	useEffect(() => {
		if (items && items.length) markReadable(items.map(item => item?._id));
	}, [items]);

	const itemsById = useMemo(() => {
		const map = { ...resolvedItemsById };
		(items || []).forEach(item => {
			const id = normalizeId(item?._id);
			if (id) map[id] = item;
		});
		return map;
	}, [items, resolvedItemsById]);

	useEffect(() => {
		if (!enabled) return;

		const pending = new Set();
		const collectFrom = (item) => {
			collectLookupIds(item, getElementsForCard, { entries }).forEach(id => {
				if (itemsById[id] || attemptedIdsRef.current.has(id)) return;
				pending.add(id);
			});
		};
		(items || []).forEach(collectFrom);
		Object.values(resolvedItemsById).forEach(collectFrom);

		if (!pending.size) return;

		const ids = Array.from(pending);
		ids.forEach(id => attemptedIdsRef.current.add(id));

		const generation = generationRef.current;
		const signal = controllerRef.current?.signal;
		const chunks = [];
		for (let i = 0; i < ids.length; i += BATCH_RESOLVE_LIMIT) {
			chunks.push(ids.slice(i, i + BATCH_RESOLVE_LIMIT));
		}

		Promise.all(chunks.map(chunk =>
			api.get(API_ITEMS_URL, { params: { ids: chunk.join(',') }, signal })
				.then(({ data, headers }) => {
					// `X-Not-Readable` (the tombstone a link leaves when it ends
					// without copies): these ids render "No longer available",
					// app-wide, instead of going blank.
					markNotReadable(parseNotReadableHeader(headers));
					const rows = Array.isArray(data) ? data : [];
					markReadable(rows.map(row => row?._id));
					return rows;
				})
				.catch(() => [])
		)).then(results => {
			// Only a pool reset makes a batch stale. A dependency change must not
			// discard it: these ids are already marked attempted, so dropping the
			// response would leave them unresolved (rendering as raw ids) forever.
			if (generation !== generationRef.current) return;
			const fetched = results.flat();
			if (!fetched.length) return;
			setResolvedItemsById(prev => {
				const next = { ...prev };
				fetched.forEach(item => {
					const id = normalizeId(item?._id);
					if (id) next[id] = item;
				});
				return next;
			});
		});
	}, [items, resolvedItemsById, itemsById, getElementsForCard, enabled, entries]);

	return itemsById;
}
